import { IQuery } from '~/client/graph'
import { isSignUpPageRequested } from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project from '~/client/src/shared/models/Project'
import { UserFacingError } from '~/client/src/shared/services/ApiAuthService'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { toast } from '~/client/src/shared/utils/toast'

import LoginRootStore from '../LoginRoot.store'
import LoginEventsStore from './LoginEvents.store'

const RESET_LINK_ERROR_TOAST_TIMEOUT = 10000

export default function registerLoginEvents(
  eventsStore: LoginEventsStore,
  rootStore: LoginRootStore,
) {
  eventsStore.on(e.TRY_UNAUTHORIZED_MODE, state => {
    // QR-code signup is a public path — the shared
    // TRY_UNAUTHORIZED_MODE_RESULT handler (commonEvents) bounces to
    // displayLoginView when isUnauthorizedMode stays false, dropping
    // the /project/:code/sign-up path and its return_to/tenant_hint
    // query params. Mirrors the desktop + mobile fix; needs to live
    // here too because login/container.ts registers loginEvents after
    // commonEvents and BaseEvents.eventMap is last-wins.
    if (isSignUpPageRequested()) {
      state.isUnauthorizedMode = true
    }

    return {
      dispatch: [e.TRY_UNAUTHORIZED_MODE_RESULT],
    }
  })

  eventsStore.on(e.VERIFY_PASSWORD_RESET_CODE, (_, resetCode) => {
    return {
      cmd: {
        cmd: {
          execute: () => {
            return new Promise((res, rej) => {
              try {
                setTimeout(() => {
                  rootStore.auth
                    .verifyPasswordResetCode(resetCode)
                    .then(res, rej)
                }, 500)
              } catch (error) {
                rej(error)
              }
            })
          },
        },
        onSuccess: [e.VERIFY_PASSWORD_RESET_CODE_SUCCESS, resetCode],
        onError: [e.VERIFY_PASSWORD_RESET_CODE_ERROR],
      },
    }
  })

  eventsStore.on(e.VERIFY_PASSWORD_RESET_CODE_SUCCESS, (state, resetCode) => {
    state.onAuthStateChangedCallback()
    rootStore.common.displayResetPasswordView(resetCode)

    return {
      dispatch: [e.INIT_COMPLETE],
    }
  })

  eventsStore.on(e.VERIFY_PASSWORD_RESET_CODE_ERROR, (_, error) => {
    toast.error(
      error instanceof UserFacingError
        ? error.message
        : Localization.translator.somethingWentWrongPleaseTryAgain,
      { timeout: RESET_LINK_ERROR_TOAST_TIMEOUT },
    )

    return {
      dispatchN: [
        [e.INIT_AUTH_USER],
        [e.REQUEST_ERROR, e.VERIFY_PASSWORD_RESET_CODE, error],
      ],
    }
  })

  eventsStore.on(
    e.CONFIRM_RESET_PASSWORD,
    (_, resetCode, newPassword, onError) => {
      return {
        cmd: {
          cmd: {
            execute: () => {
              return rootStore.auth.confirmPasswordReset(resetCode, newPassword)
            },
          },
          onSuccess: [e.CONFIRM_RESET_PASSWORD_SUCCESS],
          onError: [e.RESET_PASSWORD_ERROR, onError],
        },
      }
    },
  )

  eventsStore.on(e.CONFIRM_RESET_PASSWORD_SUCCESS, () => {
    rootStore.common.displayLoginView()
    return {
      dispatch: [e.INIT_AUTH_USER],
    }
  })

  eventsStore.on(
    e.LOGIN_WITH_EMAIL_AND_PASSWORD,
    (
      state,
      email: string,
      password: string,
      callbackSuccess,
      callbackError,
    ) => {
      state.loading.set(e.LOGIN_WITH_EMAIL_AND_PASSWORD, true)

      return {
        cmd: {
          cmd: {
            execute: () => rootStore.auth.login(email, password),
          },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.LOGIN_WITH_EMAIL_AND_PASSWORD,
            callbackSuccess,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.LOGIN_WITH_EMAIL_AND_PASSWORD,
            callbackError,
          ],
        },
      }
    },
  )

  eventsStore.on(e.INIT_APP_SELECT_PROJECT, state => {
    const [firstProjectDto] = rootStore.projectsStore.list

    if (firstProjectDto) {
      state.activeProject = Project.fromDto(firstProjectDto)
    }

    return {
      dispatch: [e.GET_AUTH_USER_PROJECT],
    }
  })

  eventsStore.on(
    e.PROJECT_TYPE_OPTIONS_RECEIVED,
    (state, { projectTypeOptions }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS, false)
      state.projectTypeOptions =
        projectTypeOptions.data[0] || state.getDefaultProjectTypeOptions()

      return {
        dispatchN: [[e.LISTEN_TO_PROJECT_TYPE_OPTIONS]],
      }
    },
  )

  eventsStore.on(e.INIT_APP_3, state => {
    state.loading.set(e.INIT_APP_2, false)
    state.didInitialize = true

    return {
      dispatch: [e.INIT_COMPLETE],
    }
  })
}
